import React from "react";
//#region types
import { InputProps, InputState } from "models/propTypes/inputPropTypes";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { CircularProgress, Grid, InputAdornment, TextField } from "@mui/material";
import Icon from "../../icon";
import StyledButton from "../../buttons/styledButton";
import NumberFormatField from "./numberFormatField";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { UtilsHelper, LocalizationHelperInstance } from "utils/helpers";

class InputField extends React.Component<InputProps, InputState> {
  constructor(props: InputProps) {
    super(props);

    this.state = {
      shrinkLabel: !UtilsHelper.isEmpty(props.inputBaseProps.value),
      variant: props.label ? "filled" : "outlined",
    };
  }

  shouldComponentUpdate(nextProps: InputProps, nextState: InputState) {
    return (
      nextProps?.inputBaseProps?.value !== this.props?.inputBaseProps?.value ||
      nextProps?.inputBaseProps?.error !== this.props?.inputBaseProps?.error ||
      nextState.shrinkLabel !== this.state.shrinkLabel ||
      nextProps?.label !== this.props.label ||
      nextProps.placeholder !== this.props.placeholder ||
      (this.props.autoCompleteInputProps !== undefined &&
        (nextProps.autoCompleteInputProps?.inputProps as any)?.value !==
          (this.props.autoCompleteInputProps?.inputProps as any)?.value) ||
      nextProps.prefix !== this.props.prefix ||
      nextProps.suffix !== this.props.suffix ||
      this.props.success !== nextProps.success ||
      this.props.disabled !== nextProps.disabled ||
      this.props.isLoading !== nextProps.isLoading ||
      this.props.autoCompleteInputProps?.InputProps.endAdornment !==
        nextProps.autoCompleteInputProps?.InputProps.endAdornment
    );
  }

  componentDidUpdate = (prevProps: InputProps) => {
    if (
      ((UtilsHelper.isEmpty(prevProps.inputBaseProps?.value) &&
        !UtilsHelper.isEmpty(this.props.inputBaseProps?.value)) ||
        (UtilsHelper.isEmpty((prevProps.autoCompleteInputProps?.inputProps as any)?.value) &&
          !UtilsHelper.isEmpty((this.props.autoCompleteInputProps?.inputProps as any)?.value))) &&
      !this.state.shrinkLabel
    ) {
      this.setShrinkLabel(true);
    } else if (
      ((!UtilsHelper.isEmpty(prevProps.inputBaseProps?.value) &&
        UtilsHelper.isEmpty(this.props.inputBaseProps?.value)) ||
        (!UtilsHelper.isEmpty((prevProps.autoCompleteInputProps?.inputProps as any)?.value) &&
          UtilsHelper.isEmpty((this.props.autoCompleteInputProps?.inputProps as any)?.value))) &&
      this.state.shrinkLabel
    ) {
      this.setShrinkLabel(false);
    }
  };

  setShrinkLabel = (val: boolean) => {
    this.setState({ ...this.state, shrinkLabel: val });
  };

  onFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    this.props.inputBaseProps.onFocus && this.props.inputBaseProps.onFocus(event);
    this.setShrinkLabel(true);
  };

  onBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    this.props.inputBaseProps.onBlur && this.props.inputBaseProps.onBlur(event);
    if (
      (UtilsHelper.isEmpty(this.props.inputBaseProps.value) || this.props.inputBaseProps.value === "undefined") &&
      UtilsHelper.isEmpty((this.props.autoCompleteInputProps?.inputProps as any)?.value)
    ) {
      this.setShrinkLabel(false);
    }
  };

  onChange = (event: React.ChangeEvent<any>) => {
    event.persist && event.persist();
    this.props.inputBaseProps.onChange && this.props.inputBaseProps.onChange(event);
    const target = event.target;
    const newValue = target.type === "checkbox" ? target.checked : target.value;
    this.props.onChangeCustom && this.props.onChangeCustom(newValue, target.formattedValue ?? newValue);
  };

  parts = LocalizationHelperInstance.getNumberSeparators();

  render() {
    return (
      <span className={clsx(cssExports.iconInputContainer, this.props.rootClass, this.props.className)}>
        <TextField
          {...this.props.autoCompleteInputProps}
          fullWidth
          InputProps={{
            ...this.props.inputBaseProps,
            ...this.props.autoCompleteInputProps?.InputProps,
            inputComponent: this.props.isNumericString ? NumberFormatField : undefined,

            startAdornment: this.props.icon && !this.props.endIcon && (
              <InputAdornment position="start" className={cssExports.inputAdornedStart}>
                <Icon icon={this.props.icon} className={cssExports.inputIcon} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {this.props.inputBaseProps.endAdornment}
                {this.props.autoCompleteInputProps?.InputProps.endAdornment}

                {this.props.onCopyToClipBoard && (
                  <InputAdornment position="end" className={cssExports.inputAdornedStart}>
                    <StyledButton
                      size={"xs"}
                      className={clsx(cssExports.iconButton, cssExports.transparent)}
                      onClick={this.props.onCopyToClipBoard}
                    >
                      <Icon icon={IconDefinitionConstants.WCopy} />
                    </StyledButton>
                  </InputAdornment>
                )}

                {this.props.icon && this.props.endIcon && (
                  <InputAdornment position="end" className={cssExports.inputAdornedStart}>
                    <Icon icon={this.props.icon} className={cssExports.inputIcon} />
                  </InputAdornment>
                )}

                {this.props.isLoading && (
                  <InputAdornment position="end" className={cssExports.inputAdornedEnd}>
                    <CircularProgress size={20} className={cssExports.inputProgress} />
                  </InputAdornment>
                )}

                {this.props.disabled ? (
                  <InputAdornment position="end" className={clsx(cssExports.inputAdornedEnd, cssExports.disabledIcon)}>
                    <Icon icon={IconDefinitionConstants.WLock} className={cssExports.inputIcon} />
                  </InputAdornment>
                ) : this.props.success ? (
                  <InputAdornment position="end" className={clsx(cssExports.inputAdornedEnd, cssExports.success)}>
                    <Icon icon={IconDefinitionConstants.WCheck} className={cssExports.inputIcon} />
                  </InputAdornment>
                ) : this.props.inputBaseProps.error ? (
                  <InputAdornment position="end" className={clsx(cssExports.inputAdornedEnd, cssExports.error)}>
                    <Icon icon={IconDefinitionConstants.WExclamation} className={cssExports.inputIcon} />
                  </InputAdornment>
                ) : (
                  <></>
                )}
              </>
            ),
            classes: {
              root: clsx(
                cssExports.inputRoot,
                this.props.icon ? cssExports.iconInput : "",
                this.props.inputBaseProps.multiline ? cssExports.inputMultilineRoot : "",
                this.props.inputBaseProps.readOnly ? cssExports.readOnlyInput : "",
                this.state.shrinkLabel && this.props.label ? cssExports.shrinkedLabelInput : "",
                this.props.labelPlacement === "right" && cssExports.inputRight
              ),
              input: cssExports.inputInput,
              focused: cssExports.focusedInput,
              error: cssExports.errorInput,
              disabled: cssExports.disabledInput,
              notchedOutline: cssExports.notchedOutline,
            },
            onChange: this.onChange,
            onBlur: this.onBlur,
            onFocus: this.onFocus,
          }}
          InputLabelProps={{
            shrink: this.state.shrinkLabel,
            classes: {
              root: clsx(
                cssExports.inputLabel,
                this.props.icon ? cssExports.iconInputLabel : "",
                this.props.inputBaseProps.readOnly ? cssExports.readOnlyLabel : "",
                this.props.labelPlacement === "right" && cssExports.labelRight
              ),
              error: cssExports.errorLabel,
              shrink: cssExports.shrinkedLabel,
              focused: cssExports.focusedLabel,
            },
          }}
          inputProps={{
            ...this.props.autoCompleteInputProps?.inputProps,
            maxLength:
              this.props.format === undefined
                ? this.props.maxLength ||
                  (this.props.isNumericString
                    ? (this.props.decimalPoints ? 21 : 11) +
                      (this.props.prefix ? this.props.prefix.length : 0) +
                      (this.props.suffix ? this.props.suffix.length : 0) +
                      (this.props.decimalPoints ? this.props.decimalPoints + 1 : 0)
                    : undefined)
                : undefined,
            style: { textAlign: this.props.labelPlacement },
            ...(this.props.isNumericString
              ? {
                  getInputRef: (this.props.autoCompleteInputProps?.inputProps as any)?.ref,
                  allowNegative: this.props.allowNegative,
                  decimalScale: this.props.decimalPoints ?? 0,
                  fixedDecimalScale: this.props.fixedDecimalScale,
                  thousandSeparator: this.parts.thousandSeparator,
                  allowLeadingZeros: this.props.allowLeadingZeros,
                  suffix: this.props.suffix ? ` ${this.props.suffix}` : undefined,
                  prefix: this.props.prefix ? `${this.props.prefix} ` : undefined,
                  decimalSeparator: this.parts.decimalSeparator,
                  format: this.props.format,
                  max: this.props.maxNumber,
                }
              : {}),
          }}
          FormHelperTextProps={{
            classes: {
              root: cssExports.inputHelperText,
              error: cssExports.errorHelperText,
            },
          }}
          label={
            this.props.labelPlacement === "right" ? (
              <Grid container justifyContent="flex-end" className={this.props.disabled ? cssExports.disableLabel : ""}>
                {this.props.label}
              </Grid>
            ) : this.state.shrinkLabel ? (
              this.props.label
            ) : (
              (this.props.placeholder ?? this.props.label)
            )
          }
          error={this.props.inputBaseProps.error}
          variant={this.state.variant as any}
          autoComplete={this.props.isNumericString || this.props.browserAutocompleteOff ? "off" : "on"}
        />
      </span>
    );
  }
}

export default InputField;
